// "Свободни апартаменти": одобрени обяви с филтри (област/населено място, цена, стаи).
// Наемателят се свързва чрез вътрешния чат; управляващият вижда и своите
// обяви със статусите им.

import React, { useCallback, useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money } from '../api';
import { useAuth } from '../AuthContext';
import { showError } from '../toast';
import { showPhoto } from '../photo-viewer';
import { BG_DISTRICTS } from '../constants';
import { cityLabel, Place, REGIONS } from '../geo';
import { SettlementPicker } from '../settlement-picker';
import { Badge, Button, Card, COLORS, Input, Muted, Screen, Select, Title } from '../ui';
import { t } from '../i18n';

// Етикетите на „всички“ се превеждат — стойността се държи в state и се сравнява,
// затова се изчисляват в компонента (езикът е фиксиран за живота на екрана).
const listingStatus = (): Record<string, [string, string]> => ({
  DRAFT: [t('Чернова'), COLORS.muted],
  PENDING_REVIEW: [t('Чака одобрение'), '#D97706'],
  APPROVED: [t('Публикувана ✓'), COLORS.success],
  REJECTED: [t('Отхвърлена'), COLORS.danger],
  ARCHIVED: [t('Архивирана'), COLORS.muted],
});

// Обява без снимки: спретнат placeholder; на наемодателя — подкана да добави.
function PhotoPlaceholder({ mine }: { mine: boolean }) {
  return (
    <View style={{ marginBottom: 8 }}>
      <View
        style={{
          height: 110,
          borderRadius: 10,
          backgroundColor: COLORS.bg,
          borderWidth: 1,
          borderColor: COLORS.border,
          borderStyle: 'dashed',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ fontSize: 28 }}>🏠</Text>
        <Text style={{ color: COLORS.muted, fontSize: 13, marginTop: 2 }}>{t('Без снимки')}</Text>
      </View>
      {mine && <Muted>{t('📸 Обявите със снимки получават повече запитвания — добавете снимки.')}</Muted>}
    </View>
  );
}

export default function ListingsScreen({ navigation }: any) {
  const { user } = useAuth();
  const isManager = user?.role === 'LANDLORD' || user?.role === 'AGENCY';
  const ALL_REGIONS = t('Всички области');
  const ALL_DISTRICTS = t('Всички квартали');
  const LISTING_STATUS = listingStatus();
  const [tab, setTab] = useState<'all' | 'mine'>('all');
  const [listings, setListings] = useState<any[]>([]);
  const [region, setRegion] = useState(ALL_REGIONS);
  // конкретно населено място в областта (по избор)
  const [place, setPlace] = useState<Place | null>(null);
  const city = place?.city ?? '';
  const [maxPrice, setMaxPrice] = useState('');
  const [minRooms, setMinRooms] = useState('');
  const [district, setDistrict] = useState(ALL_DISTRICTS);

  const load = useCallback(() => {
    if (tab === 'mine') {
      api('/listings/mine').then((r) => setListings(r.listings)).catch((e) => showError(e.message));
      return;
    }
    const params = new URLSearchParams();
    if (region !== ALL_REGIONS) params.set('region', region);
    if (city) params.set('city', city);
    if (district !== ALL_DISTRICTS) params.set('district', district);
    if (maxPrice) params.set('maxPriceCents', String(Math.round(Number(maxPrice.replace(',', '.')) * 100)));
    if (minRooms) params.set('minRooms', minRooms);
    const qs = params.toString();
    api(`/listings${qs ? `?${qs}` : ''}`).then((r) => setListings(r.listings)).catch((e) => showError(e.message));
  }, [tab, region, city, district, maxPrice, minRooms]);
  useFocusEffect(load);

  return (
    <Screen>
      <Title>{t('Свободни апартаменти')}</Title>

      {isManager && (
        <View style={{ flexDirection: 'row', backgroundColor: COLORS.bg, borderRadius: 10, padding: 4, marginBottom: 10 }}>
          {(
            [
              ['all', t('Всички обяви')],
              ['mine', t('Моите обяви')],
            ] as const
          ).map(([key, label]) => (
            <Pressable
              key={key}
              onPress={() => setTab(key)}
              style={{ flex: 1, paddingVertical: 10, borderRadius: 8, alignItems: 'center', backgroundColor: tab === key ? COLORS.card : 'transparent' }}
            >
              <Text style={{ fontWeight: tab === key ? '700' : '400', color: COLORS.text }}>{label}</Text>
            </Pressable>
          ))}
        </View>
      )}

      {tab === 'all' && (
        <>
          <Select
            placeholder={t('Област')}
            options={[ALL_REGIONS, ...REGIONS]}
            value={region}
            onChange={(r: string) => {
              setRegion(r);
              setPlace(null); // населеното място е в рамките на областта
              setDistrict(ALL_DISTRICTS); // кварталите са различни за всеки град
            }}
          />
          {region !== ALL_REGIONS && (
            <View style={{ marginTop: 8 }}>
              <SettlementPicker
                fixedRegion={region}
                value={place}
                clearable
                placeholder={t('Всички населени места')}
                onChange={(p) => {
                  setPlace(p);
                  setDistrict(ALL_DISTRICTS);
                }}
              />
            </View>
          )}
          {!!city && (BG_DISTRICTS[city]?.length ?? 0) > 0 && (
            <View style={{ marginTop: 8 }}>
              <Select
                placeholder={t('Квартал')}
                options={[ALL_DISTRICTS, ...(BG_DISTRICTS[city] ?? [])]}
                value={district}
                onChange={setDistrict}
              />
            </View>
          )}
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 8, marginBottom: 10 }}>
            <View style={{ flex: 1 }}>
              <Input placeholder={t('Макс. цена (€)')} keyboardType="decimal-pad" value={maxPrice} onChangeText={setMaxPrice} />
            </View>
            <View style={{ flex: 1 }}>
              <Input placeholder={t('Мин. стаи')} keyboardType="number-pad" value={minRooms} onChangeText={setMinRooms} />
            </View>
          </View>
        </>
      )}

      {listings.length === 0 && (
        <Card>
          <Muted>{tab === 'mine' ? t('Нямате обяви. Публикувайте от екрана на имота.') : t('Няма обяви по тези критерии.')}</Muted>
        </Card>
      )}

      {listings.map((l) => (
        <Card key={l.id}>
          {tab === 'mine' && (
            <View style={{ alignItems: 'flex-start', marginBottom: 6 }}>
              <Badge text={LISTING_STATUS[l.status]?.[0] ?? l.status} color={LISTING_STATUS[l.status]?.[1]} />
              {l.status === 'REJECTED' && l.rejectionReason ? <Muted>{t('Причина: {reason}', { reason: l.rejectionReason })}</Muted> : null}
            </View>
          )}
          {/* Списъкът зарежда УМАЛЕНИТЕ версии (бързо на мобилна мрежа),
              а при докосване се отваря оригиналът на цял екран. */}
          {l.photos?.length > 0 ? (
            <ScrollView horizontal style={{ marginBottom: 8 }}>
              {l.photos.map((p: string, i: number) => (
                <Pressable key={p} onPress={() => showPhoto(`${API_URL}${p}`)}>
                  <Image
                    source={{ uri: `${API_URL}${l.thumbs?.[i] ?? p}` }}
                    style={{ width: 150, height: 110, borderRadius: 10, marginRight: 8 }}
                  />
                </Pressable>
              ))}
            </ScrollView>
          ) : (
            <PhotoPlaceholder mine={tab === 'mine'} />
          )}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1 }}>{l.title}</Text>
            <Text style={{ fontSize: 17, fontWeight: '700', color: COLORS.primary }}>{money(l.priceCents)}</Text>
          </View>
          <Muted>
            {cityLabel(l.city, l.region)}{l.district ? `, ${l.district}` : ''}{l.rooms ? ` · ${t('{n} стаи', { n: l.rooms })}` : ''}
            {l.createdBy
              ? ` · ${l.createdBy.agencyProfile?.companyName ?? `${l.createdBy.firstName} ${l.createdBy.lastName}`}`
              : ''}
          </Muted>
          <Text style={{ color: COLORS.text, marginTop: 6 }}>{l.description}</Text>

          {user?.role === 'TENANT' && tab === 'all' && (
            <Button
              title={t('Свържи се 💬')}
              onPress={() => navigation.navigate('Chat', { listingId: l.id, title: l.title })}
            />
          )}
          {tab === 'mine' && (l.status === 'APPROVED' || l.status === 'PENDING_REVIEW') && (
            <Button
              title={t('Свали обявата')}
              variant="danger"
              onPress={async () => {
                try {
                  await api(`/listings/${l.id}/archive`, { body: {} });
                  load();
                } catch (e: any) {
                  showError(e.message);
                }
              }}
            />
          )}
          {/* Архивирана/отхвърлена/чернова: СЪЩАТА обява се подновява или
              редактира — не се създава нова за същия апартамент. */}
          {tab === 'mine' && (l.status === 'ARCHIVED' || l.status === 'REJECTED' || l.status === 'DRAFT') && (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <View style={{ flex: 1 }}>
                <Button
                  title={t('Публикувай пак')}
                  onPress={async () => {
                    try {
                      await api(`/listings/${l.id}/submit`, { body: {} });
                      load();
                    } catch (e: any) {
                      showError(e.message);
                    }
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Button
                  title={t('Редактирай')}
                  variant="secondary"
                  onPress={() => navigation.navigate('CreateListing', { apartmentId: l.apartmentId, listingId: l.id })}
                />
              </View>
            </View>
          )}
        </Card>
      ))}
    </Screen>
  );
}
